import type { 
  GetDashboardsData,
  GetDashboardsResponse,

  PostDashboardsData,
  PostDashboardsResponse,

  GetDashboardsCountData,
  GetDashboardsCountResponse,

  GetDashboardsByIdData,
  GetDashboardsByIdResponse,

  PostDashboardsByIdSetDefaultData,
  PostDashboardsByIdSetDefaultResponse,

  PostDashboardsStatsData,
  PostDashboardsStatsResponse,

  PutDashboardsByIdData,
  PutDashboardsByIdResponse,

  DeleteDashboardsByIdData,
  DeleteDashboardsByIdResponse,

  PutDashboardsByIdPanelsByPanelIdUpdateForecastData,
  PutDashboardsByIdPanelsByPanelIdUpdateForecastResponse,

  PutDashboardsByIdUpdateTimeframeData,
  PutDashboardsByIdUpdateTimeframeResponse
} from "@/types/openapi-ts";
import type { AxiosInstance } from "axios";

export function getDashboardsApiModule(affiliatesApi: AxiosInstance) {
  return {
    /**
     * Get Dashboards
     *
     * @return {Promise<Object>} api response
     */
    getDashboards(params: GetDashboardsData['query']) {
      return affiliatesApi.get<GetDashboardsResponse>('/dashboards', { params });
    },

    /**
     * Create Dashboard
     *
     * @param {Object} data
     * @param {String} data.name - Name Dashboard
     * @param {Number} data.refresh_period_seconds - Refresh interval only [ 60, 300, 900, 1800, 3600 ]
     * @param {Object[]} data.rows - []
     * @param {String} data.rows[].name - Name Section dashboard
     * @param {Object[]} data.rows[].panels - Array card this row
     * @param {String} data.rows[].panels[].type - Type only [ line_chart, bar_chart, kpi ]
     * @param {String} data.rows[].panels[].metric_name - Name
     * @param {String} data.rows[].panels[].chart_group_by - Group by only [ daily, weekly, monthly, yearly ]
     * @param {String} data.rows[].panels[].kpi_period - Period only [ daily, weekly, monthly, yearly, rolling_30_days ]
     * @param {String} data.rows[].panels[].kpi_comparison_enabled
     * @param {String} data.rows[].panels[].kpi_comparison_period - Comparison period only [ this_vs_last, this_vs_last_td, last_vs_one_before ]
     *
     * @return {Promise<Object>} api response
     */
    createDashboards(data: PostDashboardsData['body']) {
      return affiliatesApi.post<PostDashboardsResponse>('/dashboards', data);
    },

    /**
     * Get Dashboards count
     *
     * @param {Object} params
     * @param {String} [params.search]
     *
     * @return {Promise<Object>} api response
     */
    getDashboardsCount(params: GetDashboardsCountData['body']) {
      return affiliatesApi.get<GetDashboardsCountResponse>('/dashboards/count', { params });
    },

    // /**
    //  * Get Dashboards template
    //  *
    //  * @param {Object} [params]
    //  *
    //  * @return {Promise<Object>} api response
    //  */
    // getDashboardsTemplates(params) {
    //   return affiliatesApi.get('/dashboards/templates', { params });
    // },

    /**
     * Get Dashboards by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */

    getDashboard(id: string) {
      return affiliatesApi.get<GetDashboardsByIdResponse>(`/dashboards/${id}`);
    },
    /**
     * Set Default Dashboard by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */

    setDefaultDashboard(id: string) {
      return affiliatesApi.post<PostDashboardsByIdSetDefaultResponse>(`/dashboards/${id}/set-default`);
    },

    /**
     * Get preview stats panel
     *
     * @return {Promise<Object>} api response
     */

    dashboardsStats(params: PostDashboardsStatsData['body']) {
      return affiliatesApi.post<PostDashboardsStatsResponse>('/dashboards/stats', params);
    },

    /**
     * Update Dashboard
     *
     * @param {String} id
     * @param {Object} data
     * @param {String} data.name - Name Dashboard
     * @param {Number} data.refresh_period_seconds - Refresh interval only [ 60, 300, 900, 1800, 3600 ]
     * @param {Object[]} data.rows - []
     * @param {String} data.rows[].name - Name Section dashboard
     * @param {Object[]} data.rows[].panels - Array card this row
     * @param {String} data.rows[].panels[].type - Type only [ line_chart, bar_chart, kpi ]
     * @param {String} data.rows[].panels[].metric_name - Name
     * @param {String} data.rows[].panels[].chart_group_by - Group by only [ daily, weekly, monthly, yearly ]
     * @param {String} data.rows[].panels[].kpi_period - Period only [ daily, weekly, monthly, yearly, rolling_30_days ]
     * @param {String} data.rows[].panels[].kpi_comparison_enabled
     * @param {String} data.rows[].panels[].kpi_comparison_period - Comparison period only [ this_vs_last, this_vs_last_td, last_vs_one_before ]
     *
     * @return {Promise<Object>} api response
     */
    updateDashboard(id: string, data: PutDashboardsByIdData['body']) {
      return affiliatesApi.put<PutDashboardsByIdResponse>(`/dashboards/${id}`, data);
    },

    /**
     * Delete Dashboard by id
     *
     * @param {String} id
     *
     * @return {Promise<Object>} api response
     */
    deleteDashboard(id: string) {
      return affiliatesApi.delete<DeleteDashboardsByIdResponse>(`/dashboards/${id}`);
    },

    /**
     * Update Forecast Dashboard Panel
     *
     * @param {String} id
     * @param {String} idPanel
     * @param {Object} data
     * @param {Boolean} data.enable_forecast - set forecast for panel
     *
     * @return {Promise<Object>} api response
     */
    updateDashboardPanelForecast(id: string, idPanel: string, data: PutDashboardsByIdPanelsByPanelIdUpdateForecastData['body']) {
      return affiliatesApi.put<PutDashboardsByIdPanelsByPanelIdUpdateForecastResponse>(
        `/dashboards/${id}/panels/${idPanel}/update-forecast`,
        data,
      );
    },

    /**
     * Update Timeframe Dashboard
     *
     * @param {String} id
     * @param {Object} data
     * @param {String} data.relative_timeframe - time label relative timeframe
     * @param {String} data.custom_timeframe_from - time from
     * @param {String} data.custom_timeframe_to - time to
     *
     * @return {Promise<Object>} api response
     */
    updateDashboardTimeframe(id: string, data: PutDashboardsByIdUpdateTimeframeData['body']) {
      return affiliatesApi.put<PutDashboardsByIdUpdateTimeframeResponse>(`/dashboards/${id}/update-timeframe`, data);
    },
  }
}